import { useMemo, useState } from 'react'
import { LogOut } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FilterBar, PageHeader, PermissionGate, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { useLoginHistory, useSessions, useSysAdminMutations } from '../api'

/**
 * Who is signed in now, and who tried.
 *
 * <p>A session here is a refresh-token family, not a single token: signing somebody out
 * revokes the family, so the token that has already been rotated cannot be replayed.
 */
export function SessionsPage() {
  useBreadcrumb()
  const sessions = useSessions()
  const history = useLoginHistory()
  const actions = useSysAdminMutations()
  const [search, setSearch] = useState('')
  const [outcome, setOutcome] = useState('')
  const [banner, setBanner] = useState<string | null>(null)

  const attempts = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (history.data ?? []).filter((row) => {
      if (outcome === 'success' && !row.success) return false
      if (outcome === 'failure' && row.success) return false
      return !term || row.email.toLowerCase().includes(term)
    })
  }, [history.data, search, outcome])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sessions"
        subtitle="Live sign-ins, and every attempt with the reason it failed."
      />

      {banner && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {banner}
        </p>
      )}

      <Card>
        <p className="border-b border-[var(--color-border)] p-3 text-sm font-medium text-[var(--color-heading)]">
          Live sessions
        </p>
        {sessions.isPending && <Skeleton className="m-4 h-20" />}
        {sessions.isError && (
          <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
        )}
        {sessions.isSuccess && sessions.data.length === 0 && (
          <EmptyState title="Nobody is signed in" description="No refresh token is live." />
        )}
        {sessions.isSuccess && sessions.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                <tr>
                  <th className="p-3 text-start font-medium">Account</th>
                  <th className="p-3 text-start font-medium">Started</th>
                  <th className="p-3 text-start font-medium">Expires</th>
                  <th className="p-3 text-start font-medium">From</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {sessions.data.map((session) => (
                  <tr key={session.id} className="border-b border-[var(--color-border)]">
                    <td className="p-3">{session.userEmail}</td>
                    <td className="p-3"><DateValue value={session.createdAt} withTime /></td>
                    <td className="p-3"><DateValue value={session.expiresAt} withTime /></td>
                    <td className="p-3">
                      <span className="font-mono text-xs">{session.ip ?? '—'}</span>
                      <span className="block max-w-xs truncate text-xs text-[var(--color-muted)]" title={session.userAgent ?? undefined}>
                        {session.userAgent ?? ''}
                      </span>
                    </td>
                    <td className="p-3 text-end">
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => {
                          void (async () => {
                            setBanner(null)
                            try {
                              await actions.forceLogout.mutateAsync({ userId: session.userId })
                            } catch (caught) {
                              setBanner(toApiProblem(caught)?.detail ?? 'That session could not be ended')
                            }
                          })()
                        }}
                      >
                        <LogOut className="size-4" aria-hidden="true" />
                        Sign out
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <PermissionGate permission="admin:access-log">
        <FilterBar
          search={
            <Input
              aria-label="Search sign-ins"
              placeholder="Search by email"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          }
        >
          <Select aria-label="Outcome" value={outcome} onChange={(event) => setOutcome(event.target.value)}>
            <option value="">Every attempt</option>
            <option value="success">Signed in</option>
            <option value="failure">Refused</option>
          </Select>
        </FilterBar>

        <Card>
          <p className="border-b border-[var(--color-border)] p-3 text-sm font-medium text-[var(--color-heading)]">
            Recent attempts
          </p>
          {history.isPending && <Skeleton className="m-4 h-20" />}
          {history.isError && (
            <ErrorState error={history.error} onRetry={() => void history.refetch()} />
          )}
          {history.isSuccess && attempts.length === 0 && (
            <EmptyState title="Nothing matches" description="Change the filter." />
          )}
          {history.isSuccess && attempts.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                  <tr>
                    <th className="p-3 text-start font-medium">When</th>
                    <th className="p-3 text-start font-medium">Email</th>
                    <th className="p-3 text-start font-medium">Outcome</th>
                    <th className="p-3 text-start font-medium">From</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map((row) => (
                    <tr key={row.id} className="border-b border-[var(--color-border)]">
                      <td className="p-3"><DateValue value={row.attemptedAt} withTime /></td>
                      <td className="p-3">{row.email}</td>
                      <td className="p-3">
                        <StatusBadge
                          status={row.success ? 'SUCCESS' : 'FAILED'}
                          tone={row.success ? 'success' : 'danger'}
                          label={row.success ? 'Signed in' : (row.failureReason ?? 'Refused')}
                        />
                      </td>
                      <td className="p-3 font-mono text-xs">{row.ip ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </PermissionGate>
    </div>
  )
}
